import React from 'react';
import { Calendar, Plus, Trash2, Clock } from 'lucide-react';
import FormInput from '../FormInput';
import ExamenCreneau from './ExamenCreneau';

const ExamenSession = ({ 
  session, 
  sessionIndex, 
  modules, 
  salles, 
  surveillants, // Remplace utilisateurs
  onSessionChange, 
  onCreneauChange, 
  onAddCreneau, 
  onRemoveCreneau, 
  onRemoveSession, 
  canRemoveSession 
}) => {

  const getSessionDateDisplay = () => {
    if (session.date_examen) {
      return new Date(session.date_examen).toLocaleDateString('fr-FR', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
    return 'Date non définie';
  };

  const getTotalDuration = () => {
    let totalMinutes = 0;
    session.creneaux.forEach(creneau => {
      if (creneau.heure_debut && creneau.heure_fin) {
        const debut = new Date(`2000-01-01T${creneau.heure_debut}`);
        const fin = new Date(`2000-01-01T${creneau.heure_fin}`);
        totalMinutes += (fin - debut) / (1000 * 60);
      }
    });
    if (totalMinutes > 0) {
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return `${hours}h${minutes > 0 ? minutes.toString().padStart(2, '0') : ''}`;
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Header de la session */}
      <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-6 py-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center text-white">
            <div className="bg-white/10 p-2 rounded-lg mr-3">
              <Calendar className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Session {sessionIndex + 1}</h3>
              <p className="text-indigo-100 text-sm">
                {getSessionDateDisplay()}
              </p>
            </div>
          </div>

          {/* Statistiques et actions */}
          <div className="flex items-center space-x-3 mt-3 sm:mt-0">
            <div className="flex items-center space-x-2">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                📝 {session.creneaux.length} créneau{session.creneaux.length > 1 ? 'x' : ''}
              </span>
              {getTotalDuration() && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                  <Clock className="h-3 w-3 mr-1" />
                  {getTotalDuration()}
                </span>
              )}
            </div>

            {canRemoveSession && (
              <button
                type="button"
                onClick={() => onRemoveSession(sessionIndex)}
                className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
                title="Supprimer cette session"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Contenu de la session */}
      <div className="p-6">
        {/* Date de la session */}
        <div className="mb-6">
          <FormInput
            label="Date de la session"
            type="date"
            value={session.date_examen}
            onChange={(e) => onSessionChange(sessionIndex, 'date_examen', e.target.value)}
            required
            icon={Calendar}
          />
        </div>

        {/* Liste des créneaux */}
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h4 className="text-lg font-semibold text-gray-800">
              Créneaux d'examen
            </h4>
            <button
              type="button"
              onClick={() => onAddCreneau(sessionIndex)}
              className="inline-flex items-center px-3 py-2 text-sm bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors"
            >
              <Plus className="h-4 w-4 mr-1" />
              Ajouter un créneau
            </button>
          </div>

          {session.creneaux.length === 0 ? (
            // État vide pour les créneaux
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <Clock className="h-12 w-12 mx-auto mb-3 text-gray-400" />
              <h5 className="text-lg font-medium text-gray-600 mb-2">
                Aucun créneau défini
              </h5>
              <p className="text-gray-500 mb-4">
                Ajoutez au moins un créneau pour cette session d'examen
              </p>
              <button
                type="button"
                onClick={() => onAddCreneau(sessionIndex)}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Créer le premier créneau
              </button>
            </div>
          ) : (
            // Liste des créneaux
            <div className="space-y-4">
              {session.creneaux.map((creneau, creneauIndex) => (
                <ExamenCreneau
                  key={creneauIndex}
                  creneau={creneau}
                  creneauIndex={creneauIndex}
                  sessionIndex={sessionIndex}
                  modules={modules}
                  salles={salles}
                  surveillants={surveillants}
                  onCreneauChange={onCreneauChange}
                  onRemoveCreneau={onRemoveCreneau}
                  canRemove={session.creneaux.length > 1}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamenSession; 